import { isEmpty } from 'lodash';

import { StockExchangeCodeType } from '../../models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../../models/gateway/types';
import { ChartDataPayload } from '../../store/market-data/charting/types';
import { updateBigChartCacheOnLoadChartDataCompleted } from '../../store-util/BigChartCache';
import InitialSymbolsDataLoadingStatusCache, {
  InitialSymbolsDataLoadingStatusKey,
} from '../../store-util/InitialSymbolsDataLoadingStatusCache';
import { updateSmallChartDataCache } from '../../store-util/SmallChartDataCache';
import { updateTradePriceCacheFromChartData } from '../../store-util/TradePriceCache';
import { parseBase64String, parseMarketDataSummaries } from '../../util/GatewayHelpers';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { SymbolBucketSubscriptionRequestType } from './types';

const LOG_PREFIX = '[BASE-MDS-SUBS]';
const logger = LoggerService.getLogger();

/**
 * Base Market Data Summary Subscription Service
 */
export abstract class BaseMDSSubscriptionService extends BaseSubscriptionService<SymbolBucketSubscriptionRequestType> {
  public subscribe(
    symbol: string,
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    isBigChart: boolean = false,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(bucket, stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request: SymbolBucketSubscriptionRequestType = {
        bucket,
        symbols: [ symbol ],
        stockExchangeCode,
        optionalParams: { isBigChart: String(isBigChart) },
        ...this.getDefaultRequestParams(),
      };
      this.subscripitonRequests.push(request);
      this.gatewayService.sendRequest(request);
      return;
    }

    if (requestByBucketAndExchange.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols.push(symbol); // add symbol to related list
    this.gatewayService.sendRequest({
      bucket,
      symbols: [ symbol ],
      stockExchangeCode,
      optionalParams: { isBigChart: String(isBigChart) },
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    isBigChart: boolean = false,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(bucket, stockExchangeCode);

    if (!requestByBucketAndExchange) {
      const request = {
        bucket,
        symbols,
        stockExchangeCode,
        optionalParams: { isBigChart: String(isBigChart) },
        ...this.getDefaultRequestParams(),
      };
      this.subscripitonRequests.push(request);
      this.gatewayService.sendRequest(request);
      return;
    }

    const notRequestedList = symbols.filter(item => !requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(notRequestedList)) { // All symbols are already added to subscription request list!
      return;
    }

    requestByBucketAndExchange.symbols = [ ...requestByBucketAndExchange?.symbols || [], ...notRequestedList ]; // add symbols to related list
    this.gatewayService.sendRequest({
      bucket,
      symbols: notRequestedList,
      stockExchangeCode,
      optionalParams: { isBigChart: String(isBigChart) },
      ...this.getDefaultRequestParams(),
    });
  }

  public unsubscribe(
    symbol: string,
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    isBigChart: boolean = false,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(bucket, stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given bucket and exchange!
      return;
    }

    if (!requestByBucketAndExchange.symbols.includes(symbol)) { // There is no request in the list by the given symbol!
      return;
    }

    if (this.shouldDebounce(requestByBucketAndExchange)) {
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(item => item !== symbol); // remove only given symbol from the list
    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      bucket,
      symbols: [ symbol ],
      stockExchangeCode,
      optionalParams: { isBigChart: String(isBigChart) },
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  public unsubscribeList(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    isBigChart: boolean = false,
  ): void {
    const requestByBucketAndExchange = this.getRequestByBucketAndExchange(bucket, stockExchangeCode);

    if (!requestByBucketAndExchange) { // There is no request in the list by the given bucket and exchange!
      return;
    }

    const requestedList = symbols.filter(item => requestByBucketAndExchange?.symbols.includes(item)) || [];

    if (isEmpty(requestedList)) { // The symbols are not in the subscription request list!
      return;
    }

    if (this.shouldDebounce(requestByBucketAndExchange)) {
      return;
    }

    requestByBucketAndExchange.symbols = requestByBucketAndExchange.symbols.filter(
      item => !requestedList.includes(item),
    ); // remove given symbols from the list

    this.gatewayService.sendRequest({
      ...this.getDefaultRequestParams(),
      bucket,
      symbols: requestedList,
      stockExchangeCode,
      optionalParams: { isBigChart: String(isBigChart) },
      messageType: this.getUnsubscriptionRequestType(),
    });
  }

  // TODO: HW-1162  Cover all cases with unittesting
  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};
    const { messageType: messageName, traceIdentifier, summaries: summariesRaw } = data;
    const messageType: GatewayResponseType = GatewayResponseType[messageName] as GatewayResponseType ?? messageName;

    try {
      const summaries = parseBase64String(summariesRaw);
      const parsedSummaries = parseMarketDataSummaries(summaries?.rows ?? []);
      const parsedData = Array.isArray(parsedSummaries) ? {} : parsedSummaries || {};
      const symbols = Object.keys(parsedData);
      const symbol = symbols?.[0];
      const traceIdentifierRequest = this.gatewayService.getRequestedObjectList().find(
        item => item.messageType === this.getSubscriptionRequestType()
          && item.traceIdentifier === traceIdentifier,
      ) as SymbolBucketSubscriptionRequestType | undefined;

      if (!traceIdentifierRequest) {
        logger.debug(`${LOG_PREFIX} handleGWWebsocketMessages - DataSummaryResponse has no matching subscription request`, {
          traceIdentifier,
          messageType,
        });
        return;
      }

      const { bucket, optionalParams } = traceIdentifierRequest;
      const isBigChart = optionalParams?.isBigChart === 'true';

      if ([ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ].includes(bucket)) {
        updateTradePriceCacheFromChartData({ bucket, data: parsedData });
      }

      if (bucket === GWMarketDataSummaryBucket.OneDay) {
        updateSmallChartDataCache(parsedData, traceIdentifier);
      }

      if (isBigChart && symbol) {
        updateBigChartCacheOnLoadChartDataCompleted({
          symbol,
          bucket,
          data: parsedData[symbol],
        } as ChartDataPayload,
        traceIdentifier);
      }

      InitialSymbolsDataLoadingStatusCache.updateStatus(`chart_${bucket as '1D5m' | '3M1D'}` as InitialSymbolsDataLoadingStatusKey, true);
    } catch (error) {
      logger.error(`${LOG_PREFIX} handleGWWebsocketMessages - error parsing '${messageType}' - ${error}`);
    }
  }

  private getRequestByBucketAndExchange(
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
  ): SymbolBucketSubscriptionRequestType | undefined {
    return this.subscripitonRequests.find(
      item => item.bucket === bucket && item.stockExchangeCode === stockExchangeCode,
    );
  }
}
